/**
 * Background Removal Utility
 * Removes solid background colors from images using canvas manipulation
 */

/**
 * Detects the most common color at the edges of an image (likely background)
 * @param {HTMLImageElement} img - The image to analyze
 * @returns {Object} - RGB color object {r, g, b}
 */
const detectBackgroundColor = (img) => {
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');
  
  canvas.width = img.width;
  canvas.height = img.height;
  ctx.drawImage(img, 0, 0);
  
  // Sample colors from all four corners
  const samples = [];
  const sampleSize = 5; // Sample 5x5 pixels from each corner
  
  // Top-left
  const topLeft = ctx.getImageData(0, 0, sampleSize, sampleSize);
  // Top-right
  const topRight = ctx.getImageData(img.width - sampleSize, 0, sampleSize, sampleSize);
  // Bottom-left
  const bottomLeft = ctx.getImageData(0, img.height - sampleSize, sampleSize, sampleSize);
  // Bottom-right
  const bottomRight = ctx.getImageData(img.width - sampleSize, img.height - sampleSize, sampleSize, sampleSize);
  
  [topLeft, topRight, bottomLeft, bottomRight].forEach(imageData => {
    for (let i = 0; i < imageData.data.length; i += 4) {
      samples.push({
        r: imageData.data[i],
        g: imageData.data[i + 1],
        b: imageData.data[i + 2]
      });
    }
  });
  
  // Find the most common color
  const colorCounts = {};
  samples.forEach(color => {
    const key = `${color.r},${color.g},${color.b}`;
    colorCounts[key] = (colorCounts[key] || 0) + 1;
  });
  
  const mostCommon = Object.entries(colorCounts).sort((a, b) => b[1] - a[1])[0];
  const [r, g, b] = mostCommon[0].split(',').map(Number);
  
  return { r, g, b };
};

/**
 * Check if a pixel color is similar to the target color
 * @param {number} r1 - Red value of pixel
 * @param {number} g1 - Green value of pixel
 * @param {number} b1 - Blue value of pixel
 * @param {number} r2 - Red value of target
 * @param {number} g2 - Green value of target
 * @param {number} b2 - Blue value of target
 * @param {number} tolerance - Color similarity tolerance (0-255)
 * @returns {boolean}
 */
const isColorSimilar = (r1, g1, b1, r2, g2, b2, tolerance = 30) => {
  const diff = Math.sqrt(
    Math.pow(r1 - r2, 2) +
    Math.pow(g1 - g2, 2) +
    Math.pow(b1 - b2, 2)
  );
  return diff < tolerance;
};

/**
 * Removes background color from an image
 * @param {string} imageUrl - The image data URL or blob URL
 * @param {Object} options - Options for background removal
 * @param {number} options.tolerance - Color similarity tolerance (default: 30)
 * @param {boolean} options.autoDetect - Auto-detect background color (default: true)
 * @param {Object} options.targetColor - Specific color to remove {r, g, b} (optional)
 * @returns {Promise<string>} - Returns a new image data URL with transparent background
 */
export const removeBackground = async (imageUrl, options = {}) => {
  const {
    tolerance = 30,
    autoDetect = true,
    targetColor = null
  } = options;

  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    
    img.onload = () => {
      try {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        
        canvas.width = img.width;
        canvas.height = img.height;
        
        // Draw the original image
        ctx.drawImage(img, 0, 0);
        
        // Get image data
        const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
        const data = imageData.data;
        
        // Detect or use provided background color
        const bgColor = autoDetect ? detectBackgroundColor(img) : targetColor;
        
        if (!bgColor) {
          console.warn('No background color detected or provided');
          resolve(imageUrl); // Return original if no color to remove
          return;
        }
        
        console.log('Removing background color:', bgColor);
        
        // Process each pixel
        for (let i = 0; i < data.length; i += 4) {
          const r = data[i];
          const g = data[i + 1];
          const b = data[i + 2];
          const a = data[i + 3];
          
          // If pixel is similar to background color, make it transparent
          if (isColorSimilar(r, g, b, bgColor.r, bgColor.g, bgColor.b, tolerance)) {
            data[i + 3] = 0; // Set alpha to 0 (transparent)
          }
        }
        
        // Put the modified image data back
        ctx.putImageData(imageData, 0, 0);
        
        // Convert to data URL
        const resultUrl = canvas.toDataURL('image/png');
        resolve(resultUrl);
      } catch (error) {
        console.error('Error removing background:', error);
        reject(error);
      }
    };
    
    img.onerror = () => {
      console.error('Failed to load image for background removal');
      reject(new Error('Failed to load image'));
    };
    
    img.src = imageUrl;
  });
};

/**
 * Check if an image has a solid background (useful for detection)
 * @param {string} imageUrl - The image data URL or blob URL
 * @returns {Promise<boolean>} - Returns true if a solid background is detected
 */
export const hasSolidBackground = async (imageUrl) => {
  return new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    
    img.onload = () => {
      try {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        
        canvas.width = img.width;
        canvas.height = img.height;
        ctx.drawImage(img, 0, 0);
        
        // Check all four corners
        const sampleSize = 10;
        const corners = [
          ctx.getImageData(0, 0, sampleSize, sampleSize),
          ctx.getImageData(img.width - sampleSize, 0, sampleSize, sampleSize),
          ctx.getImageData(0, img.height - sampleSize, sampleSize, sampleSize),
          ctx.getImageData(img.width - sampleSize, img.height - sampleSize, sampleSize, sampleSize)
        ];
        
        // Check if corners have similar colors (indicating solid background)
        const firstPixel = {
          r: corners[0].data[0],
          g: corners[0].data[1],
          b: corners[0].data[2]
        };
        
        let similarCount = 0;
        corners.forEach(cornerData => {
          let cornerSimilar = true;
          for (let i = 0; i < cornerData.data.length; i += 4) {
            if (!isColorSimilar(
              cornerData.data[i],
              cornerData.data[i + 1],
              cornerData.data[i + 2],
              firstPixel.r,
              firstPixel.g,
              firstPixel.b,
              30
            )) {
              cornerSimilar = false;
              break;
            }
          }
          if (cornerSimilar) similarCount++;
        });
        
        // If 3 or more corners have similar colors, likely has solid background
        resolve(similarCount >= 3);
      } catch (error) {
        console.error('Error checking for solid background:', error);
        resolve(false);
      }
    };
    
    img.onerror = () => {
      resolve(false);
    };
    
    img.src = imageUrl;
  });
};


